import { useRequest } from 'umi';
import React, { useEffect, useState, useRef } from 'react';
import styles from './css/campCSS.less';
import { Input, Modal, Table } from 'antd';
import { IMarkingFilters, MarkingVO } from '@/types/camp/admin/markingType';
import { getPage, listFilters } from '@/utils/requests/camp/requests_marking';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import QuestionMarking from './study/questionMarking';
import QuestionMarkingView from './study/questionMarkingView';

import type { TableColumnsType, TableProps } from 'antd';
type OnChange = NonNullable<TableProps<MarkingVO>['onChange']>;
type Filters = Parameters<OnChange>[1];

interface Props {
  review: boolean;
  setMarkingNum?: (markingNum: number) => void;
}

enum MarkingType {
  /**
   * 公开
   */
  CAMP,

  /**
   * 企业
   */
  CORP_TRAINING,
}

const MarkingTable: React.FC<Props> = ({ review, setMarkingNum }) => {
  const [dataSource, setDataSource] = useState<MarkingVO[]>([]);
  const [listData, setListData] = useState<IMarkingFilters>({
    batchName: [],
    campName: [],
    nodeName: [],
  });
  const [baseInfo, setBaseInfo] = useState<MarkingVO>();
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(1);
  const [size, setSize] = useState(10);
  const [total, setTotal] = useState(10);
  const [keyword, setKeyword] = useState('');
  const [studyNodeId, setStudyNodeId] = useState('');
  const { Search } = Input;
  const [hash, setHash] = useState<number>();
  const ispageRef = useRef(true);
  const [filteredInfo, setFilteredInfo] = useState<Filters>({});
  const [typeFilter, setTypeFilter] = useState<MarkingType[]>([]);

  // 列举筛选器
  const { run: listFiltersRequest } = useRequest(
    () => {
      let params = {
        keyword: keyword,
        review: review,
        current: current,
        size: size,
        filteredInfo: filteredInfo,
        type: typeFilter.length === 1 ? typeFilter[0] : undefined,
      };
      return listFilters(params);
    },
    {
      manual: true,
      onSuccess: (res) => {
        setListData(res);
      },
    },
  );

  // 打卡营名称
  const campNameFilters = listData?.campName.map((item) => ({
    text: item,
    value: item,
  }));

  // 批次名称
  const batchNameFilters = listData?.batchName.map((item) => ({
    text: item,
    value: item,
  }));

  // 节点名称
  const nodeNameFilters = listData?.nodeName.map((item) => ({
    text: item,
    value: item,
  }));

  // 分页
  const { run: getPageRequest } = useRequest(
    () => {
      let params = {
        keyword: keyword,
        review: review,
        current: current,
        size: size,
        filteredInfo: filteredInfo,
        type: typeFilter.length === 1 ? typeFilter[0] : undefined,
      };
      return getPage(params);
    },
    {
      manual: true,
      onSuccess: (res) => {
        ispageRef.current = true;
        setLoading(false);
        setMarkingNum && setMarkingNum(res.total);
        setDataSource(res.records);
        setSize(res.size);
        setTotal(res.total);
        setCurrent(res.current);
      },
    },
  );
  useEffect(() => {
    listFiltersRequest();
    setLoading(true);
    getPageRequest();
  }, [current, keyword, filteredInfo]);

  useEffect(() => {
    listFiltersRequest();
    setOpen(false);
    getPageRequest();
  }, [hash]);

  const columns: ColumnsType<MarkingVO> = [
    {
      title: '序号',
      dataIndex: 'index',
      key: 'index',
      width: 80,
      render: (value: any, record: any, index: any) => <span key={index}>{index + 1}</span>,
    },
    {
      title: 'UID',
      dataIndex: 'uid',
      key: 'uid',
    },
    {
      title: '昵称',
      dataIndex: 'username',
      key: 'username',
      render: (value, record) => (
        <div>
          <a
            href={`https://bbs.fanruan.com/u/${record.uid}`}
            onClick={(e) => {
              e.stopPropagation();
            }}
            target="_blank"
          >
            {value}
          </a>
        </div>
      ),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (v) => (v === MarkingType.CORP_TRAINING ? '企业' : '公开'),
      filters: [
        { value: MarkingType.CORP_TRAINING, text: '企业' },
        { value: MarkingType.CAMP, text: '公开' },
      ],
      filterSearch: true,
      filteredValue: typeFilter || null,
    },
    {
      title: '打卡营名称',
      dataIndex: 'campName',
      key: 'campName',
      render: (v, item) => v ?? item.projectName,
      filters: campNameFilters,
      filterSearch: true,
      filteredValue: filteredInfo.campName || null,
      // onFilter: (value, record) => record.campName.startsWith(value as string),
    },
    {
      title: '批次名称',
      dataIndex: 'batchName',
      key: 'batchName',
      render: (v, item) => v ?? item.className,
      filters: batchNameFilters,
      filterSearch: true,
      filteredValue: filteredInfo.batchName || null,
      // onFilter: (value, record) => record.batchName.startsWith(value as string),
    },
    {
      title: '节点名称',
      dataIndex: 'nodeName',
      key: 'nodeName',
      filters: nodeNameFilters,
      filterSearch: true,
      filteredValue: filteredInfo.nodeName || null,
      // onFilter: (value, record) => record.nodeName.startsWith(value as string),
      ellipsis: true,
    },
    {
      title: '提交时间',
      dataIndex: 'submitTime',
      key: 'submitTime',
      width: 160,
      render: (value: any) => (value ? `${dayjs(value).format('YYYY-MM-DD HH:mm')}` : '--'),
    },
  ];

  const columnsTime: ColumnsType<MarkingVO> = [
    {
      title: '批阅时间',
      dataIndex: 'lastModifiedTime',
      key: 'lastModifiedTime',
      width: 160,
      render: (value: any) => (value ? `${dayjs(value).format('YYYY-MM-DD HH:mm')}` : '--'),
    },
  ];

  const columnsNew: ColumnsType<MarkingVO> = [...columns, ...(review === true ? [] : columnsTime)];

  const clearFilters = () => {
    setFilteredInfo({});
    setTypeFilter([]);
  };

  // 检查表格是否进行了筛选操作
  const areAllFieldsNull = (obj: any) => {
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== null) {
        return true;
      }
    }
    return false;
  };

  const onChange: TableProps<MarkingVO>['onChange'] = (pagination, filters, sorter, extra) => {
    const { type, ...rest } = (filters || {}) as any;
    setFilteredInfo(rest);
    setTypeFilter((type as MarkingType[] | []) ?? []);
    pagination.current && setCurrent(pagination.current);
    pagination.pageSize && setSize(pagination.pageSize);
    // console.log('params', pagination, filters, sorter, extra);
    // console.log('filters', filters);
    // console.log('order', order);
    // console.log('campStudyState', filters.campStudyState);
  };

  return (
    <>
      <div>
        <div>
          <Search
            placeholder="输入手机号/uid/昵称/姓名搜索"
            onSearch={(value) => {
              setKeyword(value), setCurrent(1);
              clearFilters();
            }}
            style={{ width: 360, margin: '6px 0 18px' }}
            allowClear
          />
        </div>
        <Table
          rowKey={(record) => record.id}
          columns={columnsNew}
          dataSource={dataSource}
          loading={loading}
          pagination={{
            total: total,
            showTotal: (total, range) => `共 ${total} 条数据`,
            pageSize: size,
            current: current,
            hideOnSinglePage: true,
            showQuickJumper: true,
          }}
          onChange={onChange}
          onRow={(record) => {
            return {
              onClick: (event) => {
                setOpen(true);
                setStudyNodeId(record.id);
                setBaseInfo(record);
              },
            };
          }}
        />
      </div>

      <Modal
        title={''}
        open={open}
        style={{ height: '90vh' }}
        destroyOnClose={true}
        wrapClassName={styles.my_modal}
        centered
        width={1200}
        onOk={() => {}}
        onCancel={() => {
          setOpen(false);
        }}
        footer={null}
      >
        {review ? (
          <QuestionMarking studyNodeId={studyNodeId} baseInfo={baseInfo} setHash={setHash} />
        ) : (
          <QuestionMarkingView studyNodeId={studyNodeId} baseInfo={baseInfo} />
        )}
      </Modal>
    </>
  );
};

export default MarkingTable;
